import React, { useEffect, useMemo, useState } from 'react';
import { generateClient } from 'aws-amplify/data';
import { Announcement } from '../types';
import PublicHeader from './PublicHeader';
import StorageImage from './StorageImage';
import { formatClubDate } from '../utils/clubTime';

const client = generateClient();

const PAGE_SIZE = 12;

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return formatClubDate(date, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

interface Props {
  onNavigateHome: () => void;
  onNavigateToDetail?: (id: string) => void;
}

/**
 * Full announcements archive. Shows every active announcement sorted by
 * pinned-first then publishDate DESC, paginated PAGE_SIZE at a time.
 */
const AnnouncementsPage: React.FC<Props> = ({ onNavigateHome, onNavigateToDetail }) => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);
        // Pull them all (Amplify list pagination would be nicer, but there
        // aren't enough announcements to warrant it yet).
        const result = await client.models.Announcement.list({
          filter: { isActive: { eq: true } },
          limit: 200,
        });
        setAnnouncements((result.data || []) as Announcement[]);
      } catch (err) {
        console.error('Error loading announcements:', err);
        setError('Failed to load announcements. Please try again.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const sorted = useMemo(() => {
    return [...announcements].sort((a, b) => {
      if (!!a.isPinned !== !!b.isPinned) return a.isPinned ? -1 : 1;
      return new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime();
    });
  }, [announcements]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const visible = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <main className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Header */}
      <PublicHeader title="Announcements" />

      <div className="max-w-5xl mx-auto py-12 px-4">
        <h1 className="text-3xl md:text-4xl font-bold text-fishing-blue mb-8">All Announcements</h1>

        {loading ? (
          <div className="text-center py-20 text-gray-600">Loading…</div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4">{error}</div>
        ) : sorted.length === 0 ? (
          <div className="text-center py-20 text-gray-600">No announcements yet.</div>
        ) : (
          <>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {visible.map((a) => (
                <li
                  key={a.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => onNavigateToDetail?.(a.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onNavigateToDetail?.(a.id);
                    }
                  }}
                  className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow cursor-pointer flex flex-col h-full"
                >
                  {a.imageUrl && (
                    <StorageImage
                      storageKey={a.imageUrl}
                      alt={a.title}
                      className="w-full aspect-[16/9] object-cover"
                    />
                  )}
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center justify-between mb-2 gap-2">
                      <h2 className="text-lg font-semibold text-fishing-blue">{a.title}</h2>
                      {a.isPinned && (
                        <span className="bg-fishing-green text-white text-xs px-2 py-1 rounded-full whitespace-nowrap">
                          Pinned
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 text-sm mb-3 flex-1">
                      {a.excerpt || (a.content ? a.content.substring(0, 220) + '…' : '')}
                    </p>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span className="bg-gray-100 px-2 py-1 rounded">
                        {a.category?.replace('_', ' ')}
                      </span>
                      <span>{formatDate(a.publishDate)}</span>
                    </div>
                    <span className="mt-3 text-fishing-blue font-semibold text-sm hover:underline self-start">
                      Read more →
                    </span>
                  </div>
                </li>
              ))}
            </ul>

            {totalPages > 1 && (
              <nav className="flex items-center justify-between mt-8" aria-label="Pagination">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 text-sm rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  ← Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 text-sm rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next →
                </button>
              </nav>
            )}
          </>
        )}
      </div>
    </main>
  );
};

export default AnnouncementsPage;
